import type { Action, Agent, Hook, Validation } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { Cron } from '@/components/flow';
import { CREATE_VALIDATION_TASK_FUNCTION_ID, HOOK_EVALUATE_PREDICTION } from '@/constants';
import {
  toActionId,
  toAgentId,
  toHookId,
  toProfileId,
  toValidationId,
  useCreateAction,
  useCreateHook,
  useCreateValidation,
  useDeleteAction,
  useDeleteHook,
  useDeleteValidation,
  useGetProfile,
  useUpdateAgent,
} from '@/hooks/api';
import { generateId } from '@/utils';

export type UseAddValidationOnSuccess = ({
  validation,
  action,
  hook,
  agent,
}: {
  validation: Validation;
  action: Action;
  hook: Hook;
  agent: Agent;
}) => void;

export type UseAddValidationOnError = ({ error }: { error: Error }) => void;

export type UseAddValidationOpts = {
  onSuccess?: UseAddValidationOnSuccess;
  onError?: UseAddValidationOnError;
};

export const useAddValidation = (opts?: UseAddValidationOpts) => {
  const { onSuccess: onSuccessFnBase, onError: onErrorFnBase } = opts ?? {};

  const {
    mutateAsync: createValidation,
    isPending: isPendingCreateValidation,
    isSuccess: isSuccessCreateValidation,
    error: errorCreateValidation,
  } = useCreateValidation();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const { mutateAsync: deleteValidation } = useDeleteValidation();
  const { mutateAsync: deleteAction } = useDeleteAction();
  const { mutateAsync: deleteHook } = useDeleteHook();

  const [error, setError] = useState<Error | null>(null);
  const { data: profile } = useGetProfile(toProfileId('me'));

  const addValidation = useCallback(
    async ({
      agentId,
      modelId,
      onSuccess: onSuccessFn,
      onError: onErrorFn,
    }: {
      agentId: string;
      modelId: string;
      onSuccess?: UseAddValidationOnSuccess;
      onError?: UseAddValidationOnError;
    }) => {
      setError(null);

      const onSuccess: UseAddValidationOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };

      const onError: UseAddValidationOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      const cleanupFns: (() => Promise<unknown>)[] = [];

      const onCleanup = async () => {
        /* cleanup in reverse order */
        for (const fn of cleanupFns.toReversed()) {
          try {
            await fn();
          } catch (error) {
            console.error(error);
          }
        }
      };

      try {
        let config;
        if (profile) {
          const cron = new Cron();

          config = {
            notifications: {
              [generateId()]: {
                emails: [profile.email],
                timezone: cron.timezone,
                type: 'email',
                when: cron.cronExpression(),
              },
            },
          };
        } else {
          config = {};
        }

        const validation = await createValidation({ config });

        cleanupFns.push(() => deleteValidation(toValidationId(validation.validationId)));

        const action = await createAction({
          config: { validationId: validation.validationId },
          functionId: CREATE_VALIDATION_TASK_FUNCTION_ID,
        });

        cleanupFns.push(() => deleteAction(toActionId(action.actionId)));

        const hook = await createHook({
          functionId: HOOK_EVALUATE_PREDICTION,
          config: { modelId },
          trigger: 'Prediction is Created',
          falseActionId: action.actionId,
        });

        cleanupFns.push(() => deleteHook(toHookId(hook.hookId)));

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, validation.validationId, action.actionId, hook.hookId],
          }),
        });

        onSuccess?.({ validation, action, hook, agent });

        return validation;
      } catch (e) {
        console.error(e);
        const error = e instanceof Error ? e : new Error('An unknown error occurred');

        onCleanup().finally(() => {
          setError(error);
          onError?.({ error });
        });
        throw error;
      }
    },
    [
      createAction,
      createHook,
      createValidation,
      deleteAction,
      deleteHook,
      deleteValidation,
      onErrorFnBase,
      onSuccessFnBase,
      profile,
      updateAgent,
    ]
  );

  const errors = useMemo(() => {
    const errors = [errorCreateValidation, errorCreateAction, errorCreateHook, errorUpdateAgent, error].filter(
      (e) => !!e
    );
    return errors.length > 0 ? errors : undefined;
  }, [error, errorCreateAction, errorCreateHook, errorCreateValidation, errorUpdateAgent]);

  return {
    addValidation,
    isPending: isPendingCreateValidation || isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateValidation && isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors,
  };
};
